/**
 * API Service for Kanban Board backend
 * Handles all communication with the Lambda backend
 */

const KB_API_URL =
  import.meta.env.VITE_KB_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_URL = KB_API_URL.endsWith('/') ? KB_API_URL.slice(0, -1) : KB_API_URL;
const BASE_PATH = '/api/kanban-board';

function normalizeRoomId(roomId: string): string {
  return roomId.trim().toUpperCase();
}

interface BackendResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  code?: string;
}

interface BackendPlayer {
  id: string;
  nickname: string;
  email: string;
  connectionId: string;
  isAdmin: boolean;
}

interface BackendComment {
  id: string;
  playerId: string;
  content: string;
  createdAt: string;
}

interface BackendActivity {
  id: string;
  title: string;
  description?: string;
  assignedTo: string;
  type?: string;
  columnId: string;
  comments: BackendComment[];
  movementHistory?: Array<{
    movedAt: string;
    movedByPlayerId: string;
    movedByEmail?: string;
    fromColumnId: string;
    toColumnId: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface BackendColumn {
  id: string;
  title: string;
  order: number;
}

interface BackendBoard {
  id: string;
  title: string;
  columns: BackendColumn[];
  assignedUsers: string[];
  taskTypes?: string[];
  activities: BackendActivity[];
  createdAt: string;
}

interface BackendRoom {
  id: string;
  name: string;
  adminConnectionId: string;
  players: BackendPlayer[];
  boards: BackendBoard[];
  createdAt: string;
}

async function requestApi<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${BASE_PATH}${path}`, init);
  const text = await response.text();

  let data: BackendResponse<T>;
  try {
    data = JSON.parse(text) as BackendResponse<T>;
  } catch {
    throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
  }

  if (!response.ok || !data.success) {
    throw new Error(data.error || `Server error: ${response.status}`);
  }

  return data.data;
}

export interface CreateRoomRequest {
  name: string;
  adminName: string;
  email: string;
}

export interface CreateRoomResponse {
  roomId: string;
  adminToken: string;
}

export interface JoinRoomRequest {
  userName: string;
  email: string;
}

export interface JoinRoomResponse {
  success: boolean;
  room: KanbanRoom;
  userToken: string;
}

export interface CreateBoardRequest {
  name: string;
  columns?: string[];
  assignedUsers?: string[];
  taskTypes?: string[];
}

export interface CreateActivityRequest {
  title: string;
  description?: string;
  columnId: string;
  assignee?: string;
  type?: string;
}

export interface UpdateActivityRequest {
  title?: string;
  description?: string;
  assignee?: string;
  type?: string;
}

export interface MoveActivityRequest {
  columnId: string;
  order?: number;
}

export interface AddCommentRequest {
  userId: string;
  userName: string;
  content: string;
}

import type { KanbanRoom, KanbanBoard, KanbanActivity } from '../types';

function toTimestamp(value: string | number): number {
  if (typeof value === 'number') return value;
  return new Date(value).getTime();
}

function buildToken(playerId: string, isAdmin: boolean): string {
  return btoa(`kanban:${playerId}:${isAdmin}`);
}

function getCurrentUserId(): string {
  const userId = sessionStorage.getItem('userId');
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return userId;
}

function decodePlayerIdFromToken(token: string): string {
  try {
    const [prefix, playerId] = atob(token).split(':');
    if (!prefix || !playerId) throw new Error('Invalid token');
    return playerId;
  } catch {
    throw new Error('Invalid admin token');
  }
}

function mapActivity(
  activity: BackendActivity,
  boardId: string,
  order: number,
  playersById: Map<string, BackendPlayer>
): KanbanActivity {
  const assigneePlayer = playersById.get(activity.assignedTo);
  const assignee = assigneePlayer?.email || assigneePlayer?.nickname || activity.assignedTo;

  return {
    id: activity.id,
    title: activity.title,
    description: activity.description,
    columnId: activity.columnId,
    boardId,
    assignee,
    type: activity.type,
    comments: activity.comments.map((comment) => ({
      id: comment.id,
      activityId: activity.id,
      userId: comment.playerId,
      userName: playersById.get(comment.playerId)?.nickname || comment.playerId,
      content: comment.content,
      createdAt: toTimestamp(comment.createdAt),
    })),
    movementHistory: activity.movementHistory?.map((entry) => ({
      movedAt: toTimestamp(entry.movedAt),
      movedByEmail: entry.movedByEmail,
      fromColumnId: entry.fromColumnId,
      toColumnId: entry.toColumnId,
    })),
    createdAt: toTimestamp(activity.createdAt),
    updatedAt: toTimestamp(activity.updatedAt),
    order,
  };
}

function mapBoard(board: BackendBoard, roomId: string, playersById: Map<string, BackendPlayer>): KanbanBoard {
  return {
    id: board.id,
    name: board.title,
    roomId,
    columns: board.columns.map((column) => ({
      id: column.id,
      name: column.title,
      boardId: board.id,
      order: column.order,
    })),
    activities: board.activities.map((activity, index) =>
      mapActivity(activity, board.id, index, playersById)
    ),
    assignedUsers: board.assignedUsers,
    taskTypes: board.taskTypes,
    createdAt: toTimestamp(board.createdAt),
  };
}

function mapRoom(room: BackendRoom): KanbanRoom {
  const playersById = new Map(room.players.map((player) => [player.id, player]));
  const admin = room.players.find((player) => player.isAdmin);

  return {
    roomId: room.id,
    name: room.name,
    createdAt: toTimestamp(room.createdAt),
    createdBy: admin?.id || room.players[0]?.id || '',
    participants: room.players.map((player) => ({
      userId: player.id,
      name: player.nickname,
      email: player.email,
      isAdmin: player.isAdmin,
      joinedAt: toTimestamp(room.createdAt),
    })),
    boards: room.boards.map((board) => mapBoard(board, room.id, playersById)),
  };
}

async function getBackendBoard(roomId: string, boardId: string): Promise<KanbanBoard> {
  const backendBoard = await requestApi<BackendBoard>(`/room/${normalizeRoomId(roomId)}/board/${boardId}`);
  const backendRoom = await requestApi<BackendRoom>(`/room/${normalizeRoomId(roomId)}`);
  const playersById = new Map(backendRoom.players.map((player) => [player.id, player]));
  return mapBoard(backendBoard, backendRoom.id, playersById);
}

/**
 * Create a new room
 */
export async function createRoom(request: CreateRoomRequest): Promise<CreateRoomResponse> {
  const room = await requestApi<BackendRoom>('/room', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: request.name,
      adminNickname: request.adminName,
      adminEmail: request.email,
    }),
  });

  const admin = room.players.find((player) => player.isAdmin) || room.players[0];
  if (!admin) throw new Error('Invalid room response');

  return {
    roomId: room.id,
    adminToken: buildToken(admin.id, true),
  };
}

/**
 * Get room by ID
 */
export async function getRoom(roomId: string): Promise<KanbanRoom> {
  const room = await requestApi<BackendRoom>(`/room/${normalizeRoomId(roomId)}`);
  return mapRoom(room);
}

/**
 * Join an existing room
 */
export async function joinRoom(roomId: string, request: JoinRoomRequest): Promise<JoinRoomResponse> {
  const result = await requestApi<{ room: BackendRoom; player: BackendPlayer }>(
    `/room/${normalizeRoomId(roomId)}/join`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nickname: request.userName,
        email: request.email,
      }),
    }
  );

  return {
    success: true,
    room: mapRoom(result.room),
    userToken: buildToken(result.player.id, !!result.player.isAdmin),
  };
}

function mapColumns(columns?: string[]): { title: string; order: number }[] | undefined {
  if (!columns || columns.length === 0) return undefined;
  return columns.map((title, index) => ({ title, order: index }));
}

async function refreshRoom(roomId: string): Promise<KanbanRoom> {
  return getRoom(roomId);
}

/**
 * Create a new board
 */
export async function createBoard(
  roomId: string,
  request: CreateBoardRequest,
  adminToken: string
): Promise<KanbanRoom> {
  const playerId = decodePlayerIdFromToken(adminToken);

  await requestApi<BackendBoard>(`/room/${normalizeRoomId(roomId)}/board`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: request.name,
      columns: mapColumns(request.columns),
      assignedUsers: request.assignedUsers,
      taskTypes: request.taskTypes,
      playerId,
    }),
  });

  return refreshRoom(roomId);
}

/**
 * Delete a board
 */
export async function deleteBoard(
  roomId: string,
  boardId: string,
  adminToken: string
): Promise<KanbanRoom> {
  const playerId = decodePlayerIdFromToken(adminToken);

  await requestApi<{ message: string }>(`/room/${normalizeRoomId(roomId)}/board/${boardId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ playerId }),
  });

  return refreshRoom(roomId);
}

/**
 * Create a new activity (task)
 */
export async function createActivity(
  roomId: string,
  boardId: string,
  request: CreateActivityRequest
): Promise<KanbanBoard> {
  const playerId = getCurrentUserId();
  const description = request.description && request.description.trim().length > 0
    ? request.description.trim()
    : 'No description';

  await requestApi<BackendActivity>(`/room/${normalizeRoomId(roomId)}/board/${boardId}/activity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: request.title,
      description,
      assignedTo: request.assignee || playerId,
      type: request.type,
      columnId: request.columnId,
      playerId,
    }),
  });

  return getBackendBoard(roomId, boardId);
}

/**
 * Update an activity
 */
export async function updateActivity(
  roomId: string,
  boardId: string,
  activityId: string,
  request: UpdateActivityRequest
): Promise<KanbanActivity> {
  const playerId = getCurrentUserId();

  const activity = await requestApi<BackendActivity>(
    `/room/${normalizeRoomId(roomId)}/board/${boardId}/activity/${activityId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: request.title,
        description: request.description,
        assignedTo: request.assignee,
        type: request.type,
        playerId,
      }),
    }
  );

  const backendRoom = await requestApi<BackendRoom>(`/room/${normalizeRoomId(roomId)}`);
  const playersById = new Map(backendRoom.players.map((player) => [player.id, player]));
  const board = backendRoom.boards.find((item) => item.id === boardId);
  const order = board?.activities.findIndex((item) => item.id === activity.id) ?? 0;

  return mapActivity(activity, boardId, order >= 0 ? order : 0, playersById);
}

/**
 * Move an activity to a different column
 */
export async function moveActivity(
  roomId: string,
  boardId: string,
  activityId: string,
  request: MoveActivityRequest
): Promise<KanbanBoard> {
  const playerId = getCurrentUserId();

  await requestApi<BackendActivity>(
    `/room/${normalizeRoomId(roomId)}/board/${boardId}/activity/${activityId}/move`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetColumnId: request.columnId,
        playerId,
      }),
    }
  );

  return getBackendBoard(roomId, boardId);
}

/**
 * Delete an activity
 */
export async function deleteActivity(
  roomId: string,
  boardId: string,
  activityId: string
): Promise<KanbanBoard> {
  const playerId = getCurrentUserId();

  await requestApi<{ message: string }>(
    `/room/${normalizeRoomId(roomId)}/board/${boardId}/activity/${activityId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId }),
    }
  );

  return getBackendBoard(roomId, boardId);
}

/**
 * Add a comment to an activity
 */
export async function addComment(
  roomId: string,
  boardId: string,
  activityId: string,
  request: AddCommentRequest
): Promise<KanbanActivity> {
  await requestApi<{ id: string; playerId: string; content: string; createdAt: string }>(
    `/room/${normalizeRoomId(roomId)}/board/${boardId}/activity/${activityId}/comment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: request.content,
        playerId: request.userId,
      }),
    }
  );

  const backendRoom = await requestApi<BackendRoom>(`/room/${normalizeRoomId(roomId)}`);
  const playersById = new Map(backendRoom.players.map((player) => [player.id, player]));
  const board = backendRoom.boards.find((item) => item.id === boardId);
  const activity = board?.activities.find((item) => item.id === activityId);

  if (!activity) {
    throw new Error('ACTIVITY_NOT_FOUND');
  }

  const order = board?.activities.findIndex((item) => item.id === activityId) ?? 0;

  return mapActivity(activity, boardId, order >= 0 ? order : 0, playersById);
}
